import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AVATARS, AvatarBadge } from "@/components/Avatars";
import { LETTER_ORDER } from "@/data/letters";
import { UserPlus, Trash2, BarChart3, X, Users, KeyRound, Volume2, VolumeX, Copy, IdCard, Sparkles, PenLine, ArrowRight } from "lucide-react";
import { ScreenTips } from "@/components/GuidedHighlights";
import { COACHMARKS } from "@/lib/coachmarks";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import ClassInsightsCard from "@/components/ClassInsightsCard";

const GRADES = ["Kindergarten", "1st Grade", "2nd Grade", "3rd Grade", "4th Grade", "5th Grade"];
const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

function AddStudentModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", username: "", password: "", grade: GRADES[1], avatar: AVATARS[0].key });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const student = await api.createStudent(form);
      toast.success(`${student.name} added! Username: ${student.username}`);
      setForm({ name: "", username: "", password: "", grade: GRADES[1], avatar: AVATARS[0].key });
      onCreated();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-md rounded-[28px] bg-white p-7 shadow-2xl border border-[#E5DECF]" data-testid="add-student-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">Add a Student</h3>
              <button onClick={onClose} data-testid="close-add-student" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              {error && <div data-testid="add-student-error" className="rounded-2xl bg-[#E05A47]/10 px-4 py-2.5 text-sm font-semibold text-[#B4402F]">{error}</div>}
              <div>
                <label className="block text-sm font-bold mb-1.5">Student Name</label>
                <input data-testid="student-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputClass} placeholder="e.g. Maya" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold mb-1.5">Username</label>
                  <input data-testid="student-username-field" value={form.username} onChange={(e) => set("username", e.target.value)} className={inputClass} placeholder="maya" required />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Password</label>
                  <input data-testid="student-password-field" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputClass} placeholder="••••••" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Grade</label>
                <select data-testid="student-grade-field" value={form.grade} onChange={(e) => set("grade", e.target.value)} className={inputClass}>
                  {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Avatar</label>
                <div className="grid grid-cols-8 gap-2">
                  {AVATARS.map((a) => (
                    <button key={a.key} type="button" onClick={() => set("avatar", a.key)} className={`flex items-center justify-center rounded-xl p-1 transition-all ${form.avatar === a.key ? "ring-2 ring-[#0D9488]" : "ring-1 ring-transparent hover:bg-[#F2ECE1]"}`}>
                      <AvatarBadge avatarKey={a.key} size={32} />
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="submit-add-student" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-3.5 text-base font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
                <UserPlus size={18} /> {busy ? "Adding..." : "Add Student"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function TeacherHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [onboard, setOnboard] = useState(null);
  const [classCode, setClassCode] = useState(null);
  const [myPlan, setMyPlan] = useState(null);
  const [onboardHidden, setOnboardHidden] = useState(() => !!localStorage.getItem(`zl_onboard_dismissed_${user?.id}`));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStudents(await api.listStudents());
    } finally {
      setLoading(false);
    }
    api.onboardingStatus().then(setOnboard).catch(() => {});
    api.getClassCode().then((d) => setClassCode(d.class_code)).catch(() => {});
    api.myResellerPlan().then((d) => setMyPlan(d.subscription || null)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismissOnboard = () => {
    if (user?.id) localStorage.setItem(`zl_onboard_dismissed_${user.id}`, "1");
    setOnboardHidden(true);
  };
  const allOnboarded = onboard && onboard.has_student && onboard.has_enabled_lesson && onboard.has_assignment;

  const toggleAutoplay = async (s) => {
    try {
      await api.setStudentAutoplay(s.id, !s.autoplay);
      setStudents((prev) => prev.map((x) => (x.id === s.id ? { ...x, autoplay: !s.autoplay } : x)));
      toast.success(`Independent mode ${!s.autoplay ? "on" : "off"} for ${s.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?\n\nDeleting this student will remove their saved progress and handwriting history. This can be restored by a Platform Administrator.`)) return;
    try {
      await api.softDeleteStudent(s.id);
      toast.success(`${s.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#0284C7]">{user?.role === "parent" ? "Parent" : "Teacher"} Dashboard</div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">My Students</h1>
          <p className="mt-1 font-body text-[#6B7280]">Add students, share their login, and track progress.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-student-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3.5 font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5">
          <UserPlus size={18} /> Add Student
        </button>
      </div>

      {/* Cursive Writing Studio quick-access */}
      <Link to="/writing" data-testid="cursive-studio-hero"
        className="group mt-4 flex flex-wrap items-center gap-4 rounded-3xl bg-gradient-to-r from-[#7A3FF2] via-[#0D9488] to-[#10B981] p-5 text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
          <PenLine size={26} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Free · unlocked</span>
            <span className="font-heading text-xl font-extrabold">Cursive Writing Studio</span>
          </div>
          <p className="mt-0.5 font-body text-sm text-white/90">Open the full 8-level cursive path — trace letters, join words, earn stickers.</p>
        </div>
        <ArrowRight size={22} className="shrink-0 transition-transform group-hover:translate-x-1" />
      </Link>

      {myPlan && (
        <Link to="/my-plan" data-testid="my-plan-link"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-3xl border-2 bg-white p-4 hover:brightness-95"
          style={{ borderColor: `${myPlan.brand?.accent_color || "#0D9488"}33` }}>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: myPlan.brand?.accent_color || "#0D9488" }}>
              <Sparkles size={20} />
            </div>
            <div>
              <div className="font-heading text-base font-extrabold text-[#1F2937]">
                Subscribed via <span className="text-[#0D9488]">{myPlan.brand?.name || "your partner"}</span>
              </div>
              <p className="font-body text-xs text-[#6B7280]">{myPlan.plan?.name} · <b className="text-[#1F2937]">{myPlan.status === "cancelling" ? "Cancelling" : "Active"}</b> — manage or cancel your subscription</p>
            </div>
          </div>
          <span className="rounded-full bg-[#F2ECE1] px-3 py-1 text-xs font-bold text-[#0D9488]">View plan →</span>
        </Link>
      )}

      {classCode && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[#E5DECF] bg-white p-5" data-testid="class-code-card">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><IdCard size={22} /></div>
            <div>
              <div className="font-heading text-lg font-extrabold text-[#1F2937]">Your class code</div>
              <p className="font-body text-xs text-[#6B7280]">Only needed when two students share the same username. Share it with families so they can log in.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="class-code-value" className="rounded-2xl bg-[#F2ECE1] px-4 py-2 font-mono text-xl font-extrabold tracking-widest text-[#0D9488]">{classCode}</span>
            <button data-testid="copy-class-code" onClick={() => { navigator.clipboard.writeText(classCode); toast.success("Class code copied"); }}
              className="rounded-xl border-2 border-[#E5DECF] p-2 text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]" aria-label="Copy class code">
              <Copy size={16} />
            </button>
          </div>
        </div>
      )}

      {onboard && !allOnboarded && !onboardHidden && (
        <OnboardingChecklist status={onboard} onAddStudent={() => setShowAdd(true)} onDismiss={dismissOnboard} />
      )}

      {students.length > 0 && <ClassInsightsCard />}

      {loading ? (
        <div className="mt-10 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading...</div>
      ) : students.length === 0 ? (
        <div className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-[#E5DECF] bg-white p-12 text-center">
          <Users size={40} color="#94A3B8" />
          <div className="mt-3 font-heading text-xl font-bold text-[#6B7280]">No students yet</div>
          <p className="mt-1 font-body text-[#94A3B8]">Add your first student to get started.</p>
          <button onClick={() => setShowAdd(true)} className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white">
            <UserPlus size={18} /> Add Student
          </button>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {students.map((s) => (
            <div key={s.id} data-testid={`student-card-${s.username}`} className="rounded-3xl border border-[#E5DECF] bg-white p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <AvatarBadge avatarKey={s.avatar} size={48} />
                  <div>
                    <div className="font-heading text-lg font-bold text-[#1F2937]">{s.name}</div>
                    <div className="text-sm text-[#6B7280]">Grade: {s.grade || "—"}</div>
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-2.5 py-0.5 text-xs font-semibold text-[#6B7280]">
                      <KeyRound size={12} /> {s.username}
                    </div>
                  </div>
                </div>
                <button onClick={() => remove(s)} data-testid={`delete-student-${s.username}`} className="rounded-xl p-2 text-[#E05A47] transition-colors hover:bg-[#E05A47]/10" aria-label={`Remove ${s.name}`}>
                  <Trash2 size={18} />
                </button>
              </div>
              <button onClick={() => toggleAutoplay(s)} data-testid={`autoplay-toggle-${s.username}`} aria-pressed={!!s.autoplay}
                className={`mt-4 flex w-full items-center justify-between rounded-2xl border-2 px-4 py-2.5 text-sm font-bold transition-colors ${s.autoplay ? "border-[#0D9488] bg-[#0D9488]/5 text-[#0D9488]" : "border-[#E5DECF] bg-white text-[#6B7280] hover:bg-[#F2ECE1]"}`}>
                <span className="inline-flex items-center gap-2">{s.autoplay ? <Volume2 size={16} /> : <VolumeX size={16} />} Independent mode (read‑aloud)</span>
                <span className={`relative h-5 w-9 rounded-full transition-colors ${s.autoplay ? "bg-[#0D9488]" : "bg-[#CBD5E1]"}`}><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${s.autoplay ? "left-[18px]" : "left-0.5"}`} /></span>
              </button>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm font-body text-[#6B7280]">
                  <span className="font-bold text-[#1F2937]">{s.skills_mastered ?? 0}</span> skills mastered
                </div>
                <button onClick={() => navigate(`/students/${s.id}`)} data-testid={`view-progress-${s.username}`} className="inline-flex items-center gap-1.5 rounded-2xl bg-[#F2ECE1] px-4 py-2 text-sm font-bold text-[#1F2937] transition-colors hover:bg-[#E5DECF]">
                  <BarChart3 size={16} /> View Progress
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddStudentModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={load} />
      <ScreenTips screenKey="teacher-home" steps={COACHMARKS["teacher-home"]} />
    </div>
  );
}
